/**
 * Export chart data… — the active chart's loaded table as a CSV.
 *
 * The owner's three decisions, each of which the obvious version gets wrong:
 *
 * - **The active chart only.** A file per chart on the layout is a zip, and the
 *   question being answered is "the numbers behind *this* chart".
 * - **Every loaded bar, not the visible window.** The window is a zoom level;
 *   somebody exporting wants the history the chart holds (`barsRange`), and a
 *   file that changed length with the mouse wheel would be a different export
 *   every time.
 * - **Indicators under their on-screen names** — the value legend's names
 *   (`legendName` with the host's `nameOf`), not the aliases. `ceLong_22_3_1` is
 *   a column name; "Long Stop" is what the reader saw, and a spreadsheet whose
 *   headers disagree with the legend is one nobody can read back to the chart.
 *
 * **Projected rows are dropped.** Past the settled edge a seasonal or forward
 * plot keeps drawing into dates that have not happened, and every OHLCV column
 * is null there. Those rows are a forecast, not data; a row is a bar when it
 * has a price.
 *
 * ## Where the table comes from
 *
 * The loaded columns live in `ChartGroupView` (`dataRef`, `xRef`) and the plot
 * names in the builder's pane plans — neither of which `App`, where the action
 * context is built, can see. So each chart **publishes a getter** here, keyed
 * by its chart group id, and the action asks for the table only when somebody
 * picks the command. Module scope, `crosshair-link.ts`'s shape: the reader and
 * the charts are in different subtrees, and copying five thousand bars upwards
 * on every load to serve a command run once a month is the wrong trade.
 *
 * The CSV itself is pure and tested here; the getter is the one untested line,
 * because it reads a SciChart builder that jsdom cannot construct.
 */

import { csvCell, exportFileName } from "./screener-export";

/** One plotted series as the export names it: a header, and the column it reads. */
export interface ExportSeries {
  /** What the value legend calls it. */
  name: string;
  /** The decoded column, by alias. */
  column: string;
}

/** Everything one chart has loaded, as the export needs it. */
export interface ChartTable {
  symbol: string;
  frequency: string;
  /** How to read `x` — unix seconds, or a synthetic row index. */
  xKind: "seconds" | "index";
  x: readonly number[];
  columns: Readonly<Record<string, readonly (number | null)[]>>;
  /** The plotted indicator series, in draw order, price and volume excluded. */
  series: readonly ExportSeries[];
}

/** The base columns, in the order every chart tool writes them. */
export const BASE_COLUMNS = ["open", "high", "low", "close", "volume"] as const;

const getters = new Map<string, () => ChartTable | undefined>();

/** A chart offers its table; returns the withdraw function. */
export function publishChartTable(chartGroupId: string, get: () => ChartTable | undefined): () => void {
  getters.set(chartGroupId, get);
  return () => {
    if (getters.get(chartGroupId) === get) getters.delete(chartGroupId);
  };
}

/** The table chart `chartGroupId` holds now, or undefined before it has loaded. */
export function chartTable(chartGroupId: string): ChartTable | undefined {
  return getters.get(chartGroupId)?.();
}

const finite = (v: number | null | undefined): v is number => typeof v === "number" && Number.isFinite(v);

/**
 * The date cell, in UTC — `ChartDataInspector`'s `formatX` rule, for its
 * reason: a local rendering would put a daily bar on the previous day west of
 * Greenwich. Midnight is a daily bar and its time is noise.
 */
export function exportDate(value: number, kind: "seconds" | "index"): string {
  if (kind === "index") return String(value);
  const at = new Date(value * 1000);
  if (Number.isNaN(at.getTime())) return String(value);
  const iso = at.toISOString();
  return iso.endsWith("T00:00:00.000Z") ? iso.slice(0, 10) : iso.slice(0, 19).replace("T", " ");
}

/** Which rows are bars — any price at all — as against a projection past the last one. */
export function barRows(table: Pick<ChartTable, "x" | "columns">): number[] {
  const prices = ["open", "high", "low", "close"].map((name) => table.columns[name]);
  const rows: number[] = [];
  for (let i = 0; i < table.x.length; i++) {
    if (prices.some((col) => finite(col?.[i]))) rows.push(i);
  }
  return rows;
}

/**
 * The CSV: `date, open, high, low, close, volume`, then one column per plotted
 * series under its on-screen name. Empty where a bar has no value — a warm-up,
 * a gap — never zero, the value legend's rule.
 *
 * A series reading a column already written (a volume plot reads `volume`) is
 * not written twice, and two series under one name are told apart with a
 * suffix rather than left as two headers a spreadsheet would collide.
 */
export function chartExportCsv(table: ChartTable): string {
  const taken = new Set<string>(BASE_COLUMNS);
  const names = new Set<string>(["date", ...BASE_COLUMNS]);
  const extra: ExportSeries[] = [];
  for (const sp of table.series) {
    if (taken.has(sp.column)) continue;
    taken.add(sp.column);
    let name = sp.name;
    for (let n = 2; names.has(name.toLowerCase()); n++) name = `${sp.name} (${n})`;
    names.add(name.toLowerCase());
    extra.push({ name, column: sp.column });
  }
  const columns = [...BASE_COLUMNS.map((c) => ({ name: c, column: c as string })), ...extra];
  const header = ["date", ...columns.map((c) => c.name)].map(csvCell).join(",");
  const lines = barRows(table).map((i) =>
    [
      csvCell(exportDate(table.x[i]!, table.xKind)),
      ...columns.map((c) => {
        const v = table.columns[c.column]?.[i];
        return finite(v) ? String(v) : "";
      }),
    ].join(","),
  );
  return [header, ...lines].join("\n");
}

/**
 * `AVGO`, `1d` → `AVGO_1d-2026-10-18.csv` — `exportFileName`'s stem and date,
 * so the two exports name their files one way. A ratio's operators become
 * hyphens there, which keeps `AAPL/SPY` from reading as a folder.
 */
export function chartExportFileName(symbol: string, frequency: string, now: Date = new Date()): string {
  return exportFileName(`${symbol}_${frequency}`, now);
}
